import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { loadMetaSdk } from "@/lib/metaSdk";
import {
  isWhatsAppSignupConfigured,
  whatsappConfig,
} from "@/lib/whatsappConfig";
import { parseEmbeddedSignupMessage } from "@/lib/whatsappSignupMessage";
import {
  useConnectWhatsAppMutation,
  useDisconnectWhatsAppMutation,
  useGetWhatsAppConnectionQuery,
} from "@/redux/services/integrations/whatsapp.api";

const MESSAGES = {
  notConfigured:
    "L'intégration WhatsApp n'est pas encore configurée sur cet environnement. Contactez le support ScaniFood pour l'activer.",
  sdkFailed:
    "Impossible de charger l'outil de connexion Meta. Vérifiez votre connexion et réessayez.",
  cancelled: "Connexion annulée.",
  incomplete: "La connexion n'a pas abouti. Veuillez réessayer.",
  requestFailed:
    "La connexion à WhatsApp a échoué. Veuillez réessayer dans un instant.",
  disconnectFailed: "La déconnexion a échoué. Veuillez réessayer.",
};

interface SignupIds {
  wabaId: string;
  phoneNumberId: string;
}

export const useWhatsAppSignup = () => {
  const isConfigured = isWhatsAppSignupConfigured();

  const { data: connection, isLoading } = useGetWhatsAppConnectionQuery(
    undefined,
    { skip: !isConfigured }
  );
  const [connect, { isLoading: isSubmitting }] = useConnectWhatsAppMutation();
  const [disconnectRequest, { isLoading: isDisconnecting }] =
    useDisconnectWhatsAppMutation();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The authorization code and the WABA ids arrive over two independent
  // channels, in either order. They are held in refs — never in state, and
  // never in Redux — until both are present.
  const codeRef = useRef<string | null>(null);
  const idsRef = useRef<SignupIds | null>(null);
  const listenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const detach = useCallback(() => {
    if (listenerRef.current) {
      window.removeEventListener("message", listenerRef.current);
      listenerRef.current = null;
    }
    codeRef.current = null;
    idsRef.current = null;
  }, []);

  useEffect(() => detach, [detach]);

  const submitWhenReady = useCallback(async () => {
    const code = codeRef.current;
    const ids = idsRef.current;
    if (!code || !ids) return;

    detach();

    const result = await connect({ code, ...ids });
    setIsPopupOpen(false);

    if ("error" in result) {
      setError(MESSAGES.requestFailed);
      return;
    }
    toast.success("WhatsApp connecté avec succès");
  }, [connect, detach]);

  const startSignup = useCallback(async () => {
    setError(null);

    // The button stays clickable without credentials so the click explains
    // itself instead of dead-ending on a disabled control.
    if (!isConfigured) {
      setError(MESSAGES.notConfigured);
      return;
    }

    setIsPopupOpen(true);

    let sdk;
    try {
      sdk = await loadMetaSdk();
    } catch {
      setIsPopupOpen(false);
      setError(MESSAGES.sdkFailed);
      return;
    }

    const handler = (event: MessageEvent) => {
      const message = parseEmbeddedSignupMessage(event);
      if (!message) return;

      if (message.event === "CANCEL") {
        detach();
        setIsPopupOpen(false);
        setError(MESSAGES.cancelled);
        return;
      }

      idsRef.current = {
        wabaId: message.wabaId,
        phoneNumberId: message.phoneNumberId,
      };
      void submitWhenReady();
    };

    // Armed before FB.login: the popup can post FINISH before the login
    // callback runs, and an unarmed listener would miss it.
    listenerRef.current = handler;
    window.addEventListener("message", handler);

    sdk.login(
      (response) => {
        const code = response?.authResponse?.code;

        if (!code) {
          // A CANCEL message may already have cleaned up and set a message;
          // an active listener means this is the only signal we got.
          if (listenerRef.current) {
            detach();
            setIsPopupOpen(false);
            setError(MESSAGES.incomplete);
          }
          return;
        }

        codeRef.current = code;
        void submitWhenReady();
      },
      {
        config_id: whatsappConfig.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "",
          sessionInfoVersion: "3",
        },
      }
    );
  }, [detach, isConfigured, submitWhenReady]);

  const disconnect = useCallback(async () => {
    setError(null);
    const result = await disconnectRequest();

    if ("error" in result) {
      setError(MESSAGES.disconnectFailed);
      return;
    }
    toast.success("WhatsApp déconnecté");
  }, [disconnectRequest]);

  return {
    connection,
    isLoading,
    isConnecting: isPopupOpen || isSubmitting,
    isDisconnecting,
    isConfigured,
    error,
    startSignup,
    disconnect,
  };
};
